import { Button } from '@any-ui-react/core';
import { type ReactNode, useMemo } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Link } from 'react-router';
import { isChunkLoadError, reloadOnChunkError } from '@/common/utils';
import { ErrorCard } from '../error-card';

const reload = () => window.location.reload();

const Fallback = ({ error }: FallbackProps) => {
  const isChunkError = useMemo(() => isChunkLoadError(error), [error]);
  const guideline = useMemo(() => {
    return isChunkError
      ? 'The application has been updated. Please reload the page to continue.'
      : 'Something went wrong while loading this page. Please reload and try again.';
  }, [isChunkError]);

  return (
    <ErrorCard
      title={isChunkError ? 'A New Version Is Available' : 'An Error Has Occurred'}
      guideline={
        <>
          <div>{guideline}</div>
          <div>
            If the error persists, please contact to&nbsp;
            <Link to="https://anymindgroup.slack.com/archives/C061MLDT7FY" target="_blank">
              #help-med
            </Link>
            &nbsp;Slack channel.
          </div>
        </>
      }
    >
      <div className="text-gray-7 text-3xs">{String(error)}</div>
      <Button className="bg-blue-6" onClick={reload}>
        Reload Page
      </Button>
    </ErrorCard>
  );
};

/**
 * Catches failures from lazily-loaded route chunks and renders a friendly
 * message instead of React's default developer-facing error screen.
 *
 * On a chunk-load error (typically an outdated build hitting now-deleted chunk
 * URLs after a deploy) it first attempts a single automatic reload to pull the
 * latest build. If the reload does not resolve it, the fallback UI with a
 * manual "Reload Page" button is shown.
 */
export const LazyLoadErrorBoundary = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary
    FallbackComponent={Fallback}
    onError={error => {
      if (isChunkLoadError(error)) reloadOnChunkError();
    }}
  >
    {children}
  </ErrorBoundary>
);
